import { useMemo } from 'react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import type { DropResult } from 'react-beautiful-dnd';
import { useFlagsService } from '@atlassian/jira-flags';
import { ADD_FORM, REORDER_FORM, REMOVE_FORM } from '../../common/ui/flags/constants.tsx';
import getErrorFlag from '../../common/ui/flags/main.tsx';
import { useActionsProvider } from '../../services/store';
import useTracking from '../tracking';

type Updaters = {
	add: (arg1: number) => void;
	reorder: (arg1: DropResult) => void;
	remove: (arg1: number) => void;
};

const useUpdateTicketTypesInGroup = (groupId: number) => {
	const { addTicketTypeRelation, reorderTicketTypes, removeTicketTypeRelation } =
		useActionsProvider();

	const { fireError, fireTrack } = useTracking();
	const { showFlag } = useFlagsService();

	return useMemo<Updaters>(
		() => ({
			add: (ticketTypeId: number) => {
				addTicketTypeRelation(groupId, ticketTypeId, (_, error) => {
					if (error) {
						showFlag(getErrorFlag(ADD_FORM, error));
						fireError('addTicketTypeToGroup', error, { ticketTypeId, groupId });
					} else {
						fireTrack('ticketTypeInGroup added', { ticketTypeId, groupId });
					}
				});
			},
			reorder: (result: DropResult) => {
				const { source, destination } = result;
				if (source && destination && source.index !== destination.index) {
					reorderTicketTypes(groupId, source.index, destination.index, (_, error) => {
						if (error) {
							showFlag(getErrorFlag(REORDER_FORM, error));
							fireError('reorderTicketTypeInGroup', error, { groupId });
						} else {
							fireTrack('ticketTypeInGroup reordered', { groupId });
						}
					});
				}
			},
			remove: (ticketTypeId: number) => {
				removeTicketTypeRelation(groupId, ticketTypeId, (_, error) => {
					if (error) {
						showFlag(getErrorFlag(REMOVE_FORM, error));
						fireError('removeTicketTypeFromGroup', error, { ticketTypeId, groupId });
					} else {
						fireTrack('ticketTypeInGroup removed', { ticketTypeId, groupId });
					}
				});
			},
		}),
		[
			addTicketTypeRelation,
			groupId,
			showFlag,
			fireError,
			fireTrack,
			reorderTicketTypes,
			removeTicketTypeRelation,
		],
	);
};

export default useUpdateTicketTypesInGroup;
